import { faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { Tag } from '@sorare/core/src/atoms/ui/Tag';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';

const XPValue = styled.span`
  padding: calc(0.25 * var(--unit)) var(--unit);
  color: var(--c-static-neutral-100);
  font-weight: var(--t-bold);
  border-radius: var(--unit);
`;

const Good = styled(XPValue)`
  background-color: var(--c-green-600);
`;

const Neutral = styled(XPValue)`
  background-color: var(--c-neutral-400);
  color: var(--c-static-neutral-1000);
`;

const Bad = styled(XPValue)`
  background-color: var(--c-red-600);
`;

const BoostedUpArrow = styled(FontAwesomeIcon).attrs({
  color: 'var(--c-green-600)',
})`
  margin-left: var(--half-unit);
`;

const BonusOrMalus = ({ value }: { value: number }) => {
  const { formatNumber } = useIntlContext();
  const roundedValue = formatNumber(value, { maximumFractionDigits: 1 });
  if (value > 0) {
    return <Good>+{roundedValue}</Good>;
  }
  if (value === 0) {
    return <Neutral>{roundedValue}</Neutral>;
  }
  return <Bad>{roundedValue}</Bad>;
};

type ExplanationProps = {
  xpMalus?: number;
  totalBonusInLineup?: number;
  boostBonus?: number;
  seasonBonus: number;
  rarityBonus: number;
  xpBonus: number;
  collectionBonus?: number;
  totalBonus: number;
};

const BonusExplanation = ({
  totalBonus,
  seasonBonus,
  rarityBonus,
  collectionBonus,
  xpBonus,
  xpMalus,
  // totalBonusInLineup comes from backend and does not contain boost bonus.
  totalBonusInLineup,
  boostBonus,
}: ExplanationProps) => {
  const extraBonus = totalBonusInLineup ? totalBonusInLineup - totalBonus : 0;
  return (
    <Vertical>
      {seasonBonus !== 0 && (
        <SBHorizontal>
          <FormattedMessage id="bonus.season" defaultMessage="Season" />
          <BonusOrMalus value={seasonBonus * 100} />
        </SBHorizontal>
      )}
      <SBHorizontal>
        <FormattedMessage id="bonus.rarity" defaultMessage="Rarity" />
        <BonusOrMalus value={rarityBonus * 100} />
      </SBHorizontal>
      <SBHorizontal>
        <FormattedMessage id="bonus.xp" defaultMessage="Xp bonus" />
        <BonusOrMalus value={xpBonus * 100} />
      </SBHorizontal>
      {!!xpMalus && (
        <SBHorizontal>
          <FormattedMessage
            id="bonus.xpMalus"
            defaultMessage="Non transferrable XP"
          />
          <BonusOrMalus value={xpMalus * 100} />
        </SBHorizontal>
      )}
      {!!collectionBonus && (
        <SBHorizontal>
          <FormattedMessage id="bonus.collection" defaultMessage="Collection" />
          <BonusOrMalus value={collectionBonus * 100} />
        </SBHorizontal>
      )}
      {extraBonus !== 0 && (
        <SBHorizontal>
          <FormattedMessage id="bonus.extra" defaultMessage="Extra" />
          <BonusOrMalus value={extraBonus * 100} />
        </SBHorizontal>
      )}
      {boostBonus && (
        <SBHorizontal>
          <FormattedMessage id="bonus.boost" defaultMessage="Boost" />
          <BonusOrMalus value={boostBonus * 100} />
        </SBHorizontal>
      )}
    </Vertical>
  );
};

export type BonusColors = 'white' | 'grey' | 'black' | 'red' | 'blue';

type Props = {
  totalBonus: number;
  color?: BonusColors;
  stroke?: boolean;
} & ExplanationProps;

export const Bonus = ({
  seasonBonus,
  rarityBonus,
  xpBonus,
  totalBonus,
  xpMalus,
  collectionBonus,
  totalBonusInLineup,
  boostBonus,
  color = 'grey',
  stroke = false,
}: Props) => {
  const {
    flags: { useCollectionBonusForScoring = false },
  } = useFeatureFlags();
  const { formatCardBonus } = useIntlContext();
  return (
    <Tooltip
      title={
        <BonusExplanation
          totalBonus={totalBonus}
          seasonBonus={seasonBonus}
          rarityBonus={rarityBonus}
          xpBonus={xpBonus}
          xpMalus={xpMalus}
          collectionBonus={useCollectionBonusForScoring ? collectionBonus : 0}
          totalBonusInLineup={totalBonusInLineup}
          boostBonus={boostBonus}
        />
      }
    >
      <Tag color={color} $stroke={stroke}>
        {formatCardBonus(
          (totalBonusInLineup || totalBonus) + (boostBonus ?? 0)
        )}
        {boostBonus && <BoostedUpArrow icon={faArrowUp} />}
      </Tag>
    </Tooltip>
  );
};
